import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import { Dimensions, Image, Platform, StyleSheet, View } from "react-native"
import { useCheckout } from "@stripe/react-stripe-js/checkout"
import { PaymentElement } from "@stripe/react-stripe-js/checkout"
import { theme } from "@common/theme/theme"
import TextM from "@common/components/text/TextM"
import ErrorTextM from "@common/components/text/ErrorTextM"
import LinkTextM from "@common/components/text/LinkTextM"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import GradientButtonM from "@common/components/button/GradientButtonM"
import { RatingStars } from "@common/components/rating/RatingStars"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { ScreenSkeleton } from "@common/screens/ScreenSkeleton"
import { useCountDown } from "@common/hooks/useCountDown"
import { formatDaysHoursMinutesSeconds } from "@data/utility/Utils"
import { SUMMER_PRICES } from "@common/utils/summer2026"
import logo from "../../assets/logo-blue.png"
import { NewSubscriptionAddressCard } from "../new-subscription/v2/components/NewSubscriptionAddressCard"
import appPreviewImage from "../../assets/offers/app-preview.png"

/**
 * Returns the next urgency deadline computed from the course start_date:
 * - Initial: start_date - 3 days at 23:59:59
 * - Extended (prolongation): start_date - 2 days at 15:59:59
 * - After both → null (no banner)
 */
const getUrgencyDeadline = (startDateStr) => {
    if (!startDateStr) return null
    const now = new Date()
    const startDate = new Date(startDateStr)

    // Initial: start_date - 3 days at 23:59:59
    const initial = new Date(startDate)
    initial.setDate(initial.getDate() - 3)
    initial.setHours(23, 59, 59, 0)

    // Extended (prolongation): start_date - 2 days at 15:59:59
    const extended = new Date(startDate)
    extended.setDate(extended.getDate() - 2)
    extended.setHours(15, 59, 59, 0)

    if (now < initial) return { deadline: initial, isExtended: false }
    if (now < extended) return { deadline: extended, isExtended: true }
    return null
}

const urgencyTimerLabel = (key) => {
    switch (key) {
        case "days":
            return "Jours"
        case "hours":
            return "Heures"
        case "minutes":
            return "Min"
        case "seconds":
            return "Sec"
    }
}

const UrgencyTimerCard = ({ label, value }) => (
    <View style={{ alignItems: "center" }}>
        <View style={styles.urgencyTimer}>
            <TextM fontWeight="Bold" color={theme.colors.white}>
                {value < 10 ? `0${value}` : value}
            </TextM>
        </View>
        <TextM fontSize={11} color={theme.colors.error} fontWeight="Medium">
            {urgencyTimerLabel(label)}
        </TextM>
    </View>
)

const UrgencyBanner = ({ startDate }) => {
    const result = useMemo(() => getUrgencyDeadline(startDate), [startDate])
    const durationInSec = result
        ? Math.max(0, Math.floor((result.deadline - new Date()) / 1000))
        : 0

    const { countdown, initializeCountdown } = useCountDown({ durationInSec })

    useEffect(() => {
        if (durationInSec > 0) initializeCountdown()
    }, [durationInSec])

    if (!result || countdown <= 0) return null

    const formatted = formatDaysHoursMinutesSeconds(countdown)
    const message = result.isExtended
        ? "Prolongation — Dernières heures pour s'inscrire :"
        : "Clôture des pré-inscriptions dans :"

    return (
        <View style={styles.urgencyBanner}>
            <TextM
                fontSize={14}
                fontWeight="Bold"
                color={theme.colors.error}
                wrap
                style={{ textAlign: "center" }}
            >
                {message}
            </TextM>
            <View style={styles.urgencyRow}>
                {Object.entries(formatted).map(
                    ([key, value]) =>
                        (key !== "days" || value > 0) && (
                            <UrgencyTimerCard
                                key={key}
                                label={key}
                                value={value}
                            />
                        )
                )}
            </View>
        </View>
    )
}

const StepItem = ({ number, title, subtitle, isActive }) => (
    <View style={styles.stepItem}>
        <View style={[styles.stepCircle, isActive && styles.stepCircleActive]}>
            <TextM
                fontSize={14}
                fontWeight="Bold"
                color={isActive ? theme.colors.white : theme.colors.grey}
                wrap
            >
                {number}
            </TextM>
        </View>
        <View style={{ flex: 1 }}>
            <TextM
                fontSize={14}
                fontWeight="Bold"
                color={isActive ? theme.colors.primary : theme.colors.grey}
                wrap
            >
                {title}
            </TextM>
            {subtitle && (
                <TextM fontSize={12} color={theme.colors.grey} wrap>
                    {subtitle}
                </TextM>
            )}
        </View>
    </View>
)

const InfoItem = ({ icon, children }) => (
    <View style={styles.infoRow}>
        <View style={styles.infoIcon}>
            <FontAwesome5
                name={icon}
                solid
                size={14}
                color={theme.colors.primary}
            />
        </View>
        <TextM fontSize={15} color={theme.colors.grey} wrap>
            {children}
        </TextM>
    </View>
)

export const StagePreInscriptionContent = ({ user, holidayCourse }) => {
    // Prix du stage servi par le backend (`holiday_courses.price`, via
    // `checkoutSession.holiday_course`) — la constante n'est qu'un repli si le
    // stage n'est pas chargé. Résolu une fois ici : la valeur est affichée à deux
    // endroits et les deux doivent citer le même montant.
    const stagePrice = holidayCourse?.price ?? SUMMER_PRICES.STAGE_PRICE

    const dimensions = useContext(DimensionsContext)
    const checkoutState = useCheckout()
    const [errorPayment, setErrorPayment] = useState(null)
    const [loadingPayment, setLoadingPayment] = useState(false)
    const paymentCardRef = useRef(null)
    const scrollViewRef = useRef(null)
    const addressRef = useRef(null)

    const startDate = holidayCourse?.start_date
        ? new Date(holidayCourse.start_date).toLocaleDateString("fr-FR", {
              day: "numeric",
              month: "long",
          })
        : ""
    const endDate = holidayCourse?.end_date
        ? new Date(holidayCourse.end_date).toLocaleDateString("fr-FR", {
              day: "numeric",
              month: "long",
          })
        : ""

    // "10h-12h" from slots[0] time + duration
    const timeSlotDisplay = useMemo(() => {
        const slots = holidayCourse?.slots
        const duration = holidayCourse?.live_session_duration
        if (!slots?.length || !duration) return "10h-12h"
        // slots[0] format: "Monday 10:00" or "Monday 10:30"
        const timePart = slots[0].split(" ")[1]
        if (!timePart) return "10h-12h"
        const [hours, minutes] = timePart.split(":").map(Number)
        const startH =
            minutes === 0
                ? `${hours}h`
                : `${hours}h${String(minutes).padStart(2, "0")}`
        const endMinutes = hours * 60 + minutes + duration
        const endH =
            endMinutes % 60 === 0
                ? `${Math.floor(endMinutes / 60)}h`
                : `${Math.floor(endMinutes / 60)}h${String(endMinutes % 60).padStart(2, "0")}`
        return `${startH}-${endH}`
    }, [holidayCourse?.slots, holidayCourse?.live_session_duration])

    // "10h" for step 3 subtitle
    const startTimeDisplay = useMemo(() => {
        const slots = holidayCourse?.slots
        if (!slots?.length) return "10h"
        const timePart = slots[0].split(" ")[1]
        if (!timePart) return "10h"
        const [hours, minutes] = timePart.split(":").map(Number)
        return minutes === 0
            ? `${hours}h`
            : `${hours}h${String(minutes).padStart(2, "0")}`
    }, [holidayCourse?.slots])

    // "lundi" from start_date day of week
    const startDayName = holidayCourse?.start_date
        ? new Date(holidayCourse.start_date).toLocaleDateString("fr-FR", {
              weekday: "long",
          })
        : "lundi"

    const scrollToPaymentCard = useCallback(() => {
        if (Platform.OS === "web") {
            paymentCardRef.current?.scrollIntoView?.({
                behavior: "smooth",
                block: "start",
            })
        } else {
            paymentCardRef.current?.measureLayout(
                scrollViewRef.current,
                (_x, y) => {
                    scrollViewRef.current?.scrollTo({ y, animated: true })
                },
                () => {}
            )
        }
    }, [])

    const handlePayment = useCallback(async () => {
        setLoadingPayment(true)
        setErrorPayment(null)

        try {
            // Save address first
            if (addressRef.current?.save) {
                const saved = await addressRef.current.save()
                if (!saved) {
                    setLoadingPayment(false)
                    return
                }
            }

            const { error } = await checkoutState.checkout.confirm()
            if (error) {
                setErrorPayment(error.message)
                scrollToPaymentCard()
            }
        } catch (err) {
            console.error(err)
            setErrorPayment("Une erreur inattendue s'est produite.")
            scrollToPaymentCard()
        } finally {
            setLoadingPayment(false)
        }
    }, [checkoutState, scrollToPaymentCard])

    return (
        <ScreenSkeleton
            withCenterContent
            withScroll
            scrollViewRef={scrollViewRef}
            contentContainerStyle={{ paddingHorizontal: 10 }}
            contentWrapperStyle={{ paddingHorizontal: 0, gap: 0 }}
            bottomContent={
                <View
                    style={{
                        width: Dimensions.get("window").width,
                        ...styles.bottomContent,
                    }}
                >
                    {errorPayment && <ErrorTextM errorData={errorPayment} />}

                    <GradientButtonM
                        loading={loadingPayment}
                        label="Réserver pour 1€"
                        icon={
                            <FontAwesome5 name="lock" size={16} color="white" />
                        }
                        onPress={handlePayment}
                    />

                    <TextM center wrap fontSize={12} color={theme.colors.grey}>
                        En cliquant, j'accepte les{" "}
                        <LinkTextM url="https://masteur.com/cgu">
                            Conditions générales d'utilisation
                        </LinkTextM>
                    </TextM>
                </View>
            }
        >
            {/* Logo */}
            <View style={styles.logoContainer}>
                <Image source={logo} style={styles.logo} resizeMode="contain" />
            </View>

            {/* Hero section */}
            <View style={styles.heroSection}>
                <View style={styles.badge}>
                    <FontAwesome5
                        name="fire"
                        solid
                        size={12}
                        color={theme.colors.error}
                    />
                    <TextM
                        fontSize={12}
                        fontWeight="Bold"
                        color={theme.colors.error}
                    >
                        PLACES LIMITÉES
                    </TextM>
                </View>

                <Image
                    source={appPreviewImage}
                    style={styles.heroImage}
                    resizeMode="contain"
                />

                <TextM
                    fontSize={26}
                    fontWeight="ExtraBold"
                    color={theme.colors.grey}
                    style={styles.title}
                    wrap
                >
                    Réservez la place de{" "}
                    <TextM
                        fontSize={26}
                        fontWeight="Black"
                        color={theme.colors.primary}
                    >
                        {user?.firstname}
                    </TextM>
                    {"\n"}pour le stage de révision
                </TextM>
            </View>

            {/* Details card — dates, program, group */}
            <View style={styles.detailsCard}>
                <InfoItem icon="calendar-alt">
                    Du{" "}
                    <TextM fontSize={15} fontWeight="Bold">
                        {startDate}
                    </TextM>{" "}
                    au{" "}
                    <TextM fontSize={15} fontWeight="Bold">
                        {endDate}
                    </TextM>
                    {"  ·  "}
                    <TextM fontSize={15} fontWeight="Bold">
                        {timeSlotDisplay}
                    </TextM>
                </InfoItem>
                <InfoItem icon="user-graduate">
                    Programme 100% personnalisé
                </InfoItem>
                <InfoItem icon="users">Groupes de 3 élèves max</InfoItem>
            </View>

            {/* Urgency countdown banner */}
            <UrgencyBanner startDate={holidayCourse?.start_date} />

            {/* Payment card — 1€ is the hero here */}
            <View ref={paymentCardRef} style={styles.paymentCard}>
                <View style={styles.priceHeader}>
                    <TextM
                        fontSize={24}
                        fontWeight="Black"
                        color={theme.colors.primary}
                    >
                        Pré-inscription : 1€
                    </TextM>
                    <TextM
                        fontSize={13}
                        color={theme.colors.grey}
                        style={{ textAlign: "center", marginTop: 2 }}
                        wrap
                    >
                        déduit du montant final du stage ({stagePrice}€)
                    </TextM>
                </View>

                <PaymentElement
                    options={{ layout: "tabs" }}
                    onChange={(event) =>
                        setErrorPayment(event?.error?.message ?? null)
                    }
                />

                <NewSubscriptionAddressCard
                    ref={addressRef}
                    userEmail={user?.email}
                />

                <View style={styles.secureBadge}>
                    <FontAwesome5
                        name="shield-alt"
                        solid
                        size={12}
                        color={theme.colors.grey}
                    />
                    <TextM fontSize={12} color={theme.colors.grey}>
                        Paiement sécurisé via Stripe
                    </TextM>
                </View>
            </View>

            {/* How it works */}
            <View style={styles.stepperCard}>
                <TextM
                    fontSize={13}
                    fontWeight="Bold"
                    color={theme.colors.grey}
                    style={{ marginBottom: 8, letterSpacing: 0.5 }}
                >
                    COMMENT ÇA MARCHE
                </TextM>
                <StepItem
                    number="1"
                    title="Pré-inscription (1€)"
                    subtitle={`Pour garantir votre place à ${stagePrice}€`}
                    isActive
                />
                <StepItem
                    number="2"
                    title="RDV personnalisé et validation"
                    subtitle="Un conseiller vous appelle sous 24h pour valider les matières et valider l'inscription avec vous."
                />
                <StepItem
                    number="3"
                    title={`Début du stage`}
                    subtitle={`Votre enfant commence ${startDayName} ${startDate} à ${startTimeDisplay}`}
                />
            </View>

            {/* Testimonials — rating + title + iframe together */}
            <View style={styles.testimonialsSection}>
                <TextM
                    fontSize={16}
                    fontWeight="Bold"
                    color={theme.colors.primary}
                    style={{ textAlign: "center", marginBottom: 6 }}
                >
                    Ce que disent les parents
                </TextM>
                <View style={styles.ratingRow}>
                    <RatingStars score={4.9} scale={5} />
                    <TextM fontSize={13} color={theme.colors.grey}>
                        20 000+ heures de cours dispensées
                    </TextM>
                </View>
                {Platform.OS === "web" && (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: `<iframe height="600px" id='testimonialto-masteur-tag-all-light-animated' src="https://embed-v2.testimonial.to/w/masteur?animated=on&theme=light&shadowColor=e7eaf4&speed=1&tag=all&cc=off" frameborder="0" scrolling="no" width="100%"></iframe>`,
                        }}
                        style={{ width: "100%", minHeight: 400, marginTop: 12 }}
                    />
                )}
            </View>
        </ScreenSkeleton>
    )
}

const styles = StyleSheet.create({
    logoContainer: {
        alignItems: "center",
        paddingTop: 20,
        paddingBottom: 10,
    },
    logo: {
        width: 150,
        height: 50,
    },
    heroSection: {
        alignItems: "center",
        gap: 16,
        paddingBottom: 10,
    },
    badge: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        backgroundColor: theme.colors.errorLight ?? "#FEE2E2",
        paddingHorizontal: 14,
        paddingVertical: 6,
        borderRadius: 20,
    },
    heroImage: {
        width: "100%",
        height: 200,
        borderRadius: 10,
    },
    title: {
        textAlign: "center",
        lineHeight: 32,
    },
    detailsCard: {
        backgroundColor: theme.colors.white,
        padding: 20,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        width: "100%",
        gap: 14,
        marginTop: 16,
    },
    infoRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
    },
    infoIcon: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor:
            theme.colors.primaryLighter ?? theme.colors.linkLighter,
        alignItems: "center",
        justifyContent: "center",
    },
    urgencyBanner: {
        backgroundColor: theme.colors.errorLight ?? "#FEE2E2",
        borderRadius: 12,
        paddingVertical: 14,
        paddingHorizontal: 16,
        width: "100%",
        alignItems: "center",
        gap: 10,
        marginTop: 16,
        borderWidth: 2,
        borderColor: theme.colors.error,
    },
    urgencyRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
    },
    urgencyTimer: {
        backgroundColor: theme.colors.error,
        width: 50,
        height: 42,
        borderRadius: 10,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 3,
    },
    paymentCard: {
        backgroundColor: theme.colors.white,
        padding: 24,
        borderRadius: 20,
        borderWidth: 2,
        borderColor: theme.colors.primary,
        width: "100%",
        gap: 16,
        marginTop: 16,
    },
    priceHeader: {
        alignItems: "center",
    },
    secureBadge: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
    },
    stepperCard: {
        backgroundColor: theme.colors.white,
        padding: 20,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        width: "100%",
        gap: 10,
        marginTop: 16,
    },
    stepItem: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 12,
    },
    stepCircle: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: theme.colors.lightMore,
        alignItems: "center",
        justifyContent: "center",
    },
    stepCircleActive: {
        backgroundColor: theme.colors.primary,
    },
    testimonialsSection: {
        width: "100%",
        marginTop: 24,
        paddingBottom: 20,
        alignItems: "center",
    },
    ratingRow: {
        alignItems: "center",
        gap: 4,
        marginBottom: 4,
    },
    bottomContent: {
        alignSelf: "center",
        gap: 10,
        padding: 5,
        paddingBottom: 10,
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
        alignItems: "center",
    },
})
